// VH-233 / VH-234: pure helpers for supplementary tests, marker history, change summaries and the draw checklist.
import { localeFor } from './cycleStatus'
import { fmtNum } from './healthReportFormat'

const present = (v) => v !== null && v !== undefined && v !== ''

/** Results of a version payload that were measured more than once (newest value used, earlier kept). */
export const markersWithHistory = (payload) =>
  ((payload?.markers?.results) ?? []).filter((r) => Array.isArray(r.history) && r.history.length > 0)

/** `> 52 nmol/l`, with the viewer's number format; the lab text for values without a number. */
export const markerValueText = (m, language) => {
  if (!m) return ''
  if (!present(m.value)) return m.value_text ?? ''
  return [m.value_operator, fmtNum(m.value, language, 2), m.unit].filter(present).join(' ')
}

/** A backend datetime (naive, local) → the value of an `<input type="datetime-local">`. */
export const toLocalInput = (iso) => (typeof iso === 'string' && iso.length >= 16 ? iso.slice(0, 16) : '')

/** `Di., 13.10., 08:00` in the viewer's language. */
export const formatDateTime = (iso, language) =>
  iso
    ? new Date(iso).toLocaleString(localeFor(language), {
        weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : ''

/** When a preparation step applies: a calendar date from the planned draw, else the relative offset. */
export const stepWhen = (t, step, language) => {
  if (step.starts_at) {
    const when = formatDateTime(step.starts_at, language)
    return step.days_before_draw > 0 ? t('healthCycle.testsToRequest.stepFrom', { when }) : t('healthCycle.testsToRequest.stepAt', { when })
  }
  if (!step.days_before_draw) return t('healthCycle.testsToRequest.stepMorning')
  if (step.days_before_draw <= 3) return t('healthCycle.testsToRequest.stepRelativeHours', { count: step.hours_before_draw })
  return t('healthCycle.testsToRequest.stepRelativeDays', { count: step.days_before_draw })
}

const TEST_LABEL_CODES = ['settle_kidney_function', 'check_effect', 'monitor', 'therapy_monitoring']

/** The test name in the viewer's language: translated rule text as sent, known kinds from i18n, else the report text. */
export const checklistTestLabel = (t, test, language) => {
  if (test.translated || language === 'en' || !TEST_LABEL_CODES.includes(test.reason_code)) return test.test
  return t(`healthCycle.testsToRequest.test.${test.reason_code}`, { markers: (test.markers ?? []).join(', '), defaultValue: test.test })
}

export const checklistReason = (t, test, language) => {
  if (test.translated || language === 'en' || !TEST_LABEL_CODES.includes(test.reason_code)) return test.reason
  return t(`healthCycle.testsToRequest.reason.${test.reason_code}`, { defaultValue: test.reason ?? '' })
}

/** Does a change summary list anything the user decides on? */
export const summaryHasItems = (s) => {
  if (!s) return false
  const groups = [s.findings, s.conditions, s.recommendations, s.next_tests]
  const lists = groups.flatMap((g) => Object.values(g ?? {}))
  // VH-236 follow-up: weekly-menu changes count as changes, even though the menu is not frozen —
  // a revision that only moved the menu used to read as "no changes at all".
  const menu = (s.menu?.weeks ?? []).some((w) => (w.added?.length ?? 0) + (w.removed?.length ?? 0) + (w.replaced?.length ?? 0) > 0)
  return menu || lists.some((l) => Array.isArray(l) && l.length > 0)
    || (s.supplementary_tests?.attached?.length ?? 0) > 0 || (s.supplementary_tests?.detached?.length ?? 0) > 0
}
